import { type FC, useState } from "react";
import { ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { enqueueSnackbar } from "notistack";

import { TOAST } from "@/shared/constants/toastMessages";

import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Tag } from "@/shared/components/ui/tag";
import { supabase } from "@/shared/lib/supabase";
import { AUTH_ROUTES } from "@/shared/constants/paths";
import { useGoogleOAuthEnabled } from "@/shared/hooks/useGoogleOAuthEnabled";
import { startGoogleOAuth } from "@/shared/utils/googleOAuth";
import { saveLastAuthMethod, getLastAuthMethod } from "@/shared/utils/lastAuthMethod";
import { acceptInvitationForExistingUser } from "@/shared/utils/invitationAcceptance";
import googleIcon from "@/shared/assets/icons/google.svg";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";

export function createInviteLogin(token: string, email: string): FC<FlowCustomComponentProps> {
  const InviteLogin: FC<FlowCustomComponentProps> = ({ onCompleteFlow }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isGoogleOAuthEnabled = useGoogleOAuthEnabled();
    const lastAuthMethod = getLastAuthMethod();

    const handleSignIn = async () => {
      if (isLoading || !password) return;
      setError(null);
      setIsLoading(true);

      try {
        const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (authError) {
          setError(authError.message);
          setIsLoading(false);
          return;
        }

        if (!authData.user) {
          setError("Failed to sign in. Please try again.");
          setIsLoading(false);
          return;
        }

        saveLastAuthMethod("password");
        const accepted = await acceptInvitationForExistingUser(token, email);

        if (!accepted) {
          setError("Failed to accept invitation. Please try again.");
          setIsLoading(false);
          return;
        }

        const { refetchCriticalQueries } = await import("@/shared/utils/queryRefetchHelper");
        await refetchCriticalQueries(queryClient);

        await queryClient.invalidateQueries({ queryKey: ["userContacts"] });
        await queryClient.invalidateQueries({
          queryKey: ["personInvitations"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["personInvitationsAccepted"],
        });

        enqueueSnackbar(TOAST.ACCOUNT_JOINED, {
          variant: "success",
        });

        onCompleteFlow?.();
      } catch (err) {
        console.error("Sign in error:", err);
        setError("Failed to sign in. Please try again.");
        sentryUtils.captureError(err, "InviteLogin sign in failed");
        sentryUtils.logAuthenticationEvent("login");
        setIsLoading(false);
      }
    };

    const handleGoogleSignIn = async () => {
      setError(null);

      const redirectPath = `${window.location.pathname}?${searchParams.toString()}`;
      const oauthError = await startGoogleOAuth({
        type: "invite-existing",
        redirectPath,
        token,
      });

      if (oauthError) {
        setError(oauthError);
      }
    };

    const handleForgotPassword = () => {
      navigate(AUTH_ROUTES.FORGOT_PASSWORD);
    };

    return (
      <div className="flex flex-col">
        {isGoogleOAuthEnabled && (
          <>
            <div className="relative">
              <Button
                variant="outline"
                rounded={true}
                className="w-full gap-2"
                onClick={handleGoogleSignIn}
              >
                <img src={googleIcon} alt="Google" className="h-4 w-4" />
                Sign in with Google
              </Button>
              {lastAuthMethod === "google" && (
                <Tag color="brand" className="absolute -top-2.5 -right-2">
                  Used last time
                </Tag>
              )}
            </div>

            <div className="my-6 flex items-center gap-4">
              <div className="h-px flex-1 bg-stone-200" />
              <span className="text-sm text-secondary">OR</span>
              <div className="h-px flex-1 bg-stone-200" />
            </div>
          </>
        )}

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label htmlFor="login-email" className="text-sm font-medium text-primary">
              Email
            </label>
            {lastAuthMethod === "password" && <Tag color="brand">Used last time</Tag>}
          </div>
          <Input
            id="login-email"
            type="email"
            value={email}
            disabled={true}
            className="bg-subtle"
          />
        </div>

        <div className="mt-4 flex flex-col gap-1.5">
          <label htmlFor="login-password" className="text-sm font-medium text-primary">
            Password
          </label>
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError(null);
              }}
              className="pr-10"
              placeholder="Enter your password"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSignIn();
              }}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleForgotPassword}
          className="mt-2 self-end text-sm text-green-800 underline"
        >
          Forgot password?
        </button>

        {error && <p className="mt-4 text-center text-sm text-destructive">{error}</p>}

        <div className="mt-10">
          <Button
            variant="brand"
            rounded={true}
            onClick={handleSignIn}
            disabled={isLoading}
            className="w-full"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Sign in & join <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  return InviteLogin;
}
